import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import Login from "./pages/Login";
import ExecuteFlow from "./pages/ExecuteFlow";
import CallLogs from "./pages/CallLogs";
import SMS from "./pages/Sms";
import IVRDashboard from "./pages/IVRDashboard";
import CreateFlow from "./pages/CreateFlow";
import AdminPanel from "./pages/AdminPanel";
import AIVoiceCall from "./pages/AIVoiceCall";
import Navigation from "./Components/Navigation";

const theme = createTheme({
  palette: {
    primary: { main: "#6366f1" },
    secondary: { main: "#8b5cf6" },
    background: { default: "#f8fafc", paper: "#ffffff" },
    text: { primary: "#0f172a", secondary: "#64748b" },
    success: { main: "#10b981" },
    error: { main: "#ef4444" },
    warning: { main: "#f59e0b" },
    info: { main: "#3b82f6" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    h4: { fontWeight: 700, letterSpacing: "-0.02em" },
    h5: { fontWeight: 700, letterSpacing: "-0.01em" },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    button: { fontWeight: 600, textTransform: "none" },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 10, padding: "10px 20px", fontWeight: 600, textTransform: "none", boxShadow: "none", "&:hover": { boxShadow: "none" } },
        containedPrimary: { background: "#6366f1", "&:hover": { background: "#4f46e5" } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 16, boxShadow: "0 1px 2px rgba(0,0,0,0.04)", border: "1px solid #e2e8f0" },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": { borderRadius: 10, fontSize: "0.9rem", "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: 2 } },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: { root: { fontSize: "0.875rem", borderColor: "#f1f5f9" } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, fontSize: "0.75rem" } },
    },
  },
});


const decodeToken = (token) => {
  try {
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return { email: payload.email || 'Unknown User', userId: payload.userId || null, role: payload.role || 'user' };
  } catch { return null; }
};

function ProtectedLayout({ token, userEmail, children }) {
  return (
    <div className="app-layout">
      <Navigation userEmail={userEmail} />
      <main className="app-main">
        {children}
      </main>
    </div>
  );
}

function App() {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [email, setEmail] = useState(localStorage.getItem("email"));
  const [role, setRole] = useState(localStorage.getItem("role") || "user");

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    const storedEmail = localStorage.getItem("email");
    const storedRole = localStorage.getItem("role");
    if (storedToken) { setToken(storedToken); setEmail(storedEmail); setRole(storedRole || "user"); }
  }, []);

  const userEmail = email || (token ? decodeToken(token)?.email : null) || "Guest";
  const userRole = role || (token ? decodeToken(token)?.role : "user");

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    localStorage.removeItem("role");
    setToken(null);
    setEmail(null);
    setRole("user");
  };

  const handleLogin = (newToken, newEmail) => {
    localStorage.setItem("token", newToken);
    localStorage.setItem("email", newEmail);
    const decoded = decodeToken(newToken);
    const newRole = decoded?.role || "user";
    localStorage.setItem("role", newRole);
    setToken(newToken);
    setEmail(newEmail);
    setRole(newRole);
  };

  const protect = (Component, props = {}) =>
    token ? (
      <ProtectedLayout token={token} userEmail={userEmail}>
        <Component token={token} userEmail={userEmail} {...props} />
      </ProtectedLayout>
    ) : (
      <Navigate to="/login" />
    );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={token ? <Navigate to="/" /> : <Login onLogin={handleLogin} />} />
          <Route path="/" element={protect(ExecuteFlow)} />
          <Route path="/call-logs" element={protect(CallLogs)} />
          <Route path="/sms" element={protect(SMS)} />
          <Route path="/ivr-dashboard" element={protect(IVRDashboard)} />
          <Route path="/create-flow" element={protect(CreateFlow)} />
          <Route path="/admin" element={userRole === "admin" ? protect(AdminPanel) : <Navigate to="/" />} />
          <Route path="/ai-voice" element={protect(AIVoiceCall)} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;